import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import HistoryIcon from '@mui/icons-material/History';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { usePortalProjects, usePortalProjectView, usePortalPhotoSrc } from '../hooks/usePortal';
import { opsApi } from '../api/client';
import type { ShareFile } from '../api/publicShare';
import { OverviewLoadingState, ProjectOverviewReport } from '../components/ProjectOverviewReport';
import type { PortalProjectSummary } from '../api/types';
import { formatDate, humanizeStatus, statusColor } from '../lib/format';
import logo from '../assets/dikonek-logo.png';

/**
 * The customer-facing portal a role=client login lands on. Overview only —
 * the same sanitized progress report the public share link renders, scoped to
 * the customer's own projects. With one running project they go straight to
 * it; with several they choose; with none they still see their project
 * history at Dikonek.
 */
export function ClientPortalPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: projects, isLoading, error } = usePortalProjects();

  if (isLoading) {
    return (
      <PortalFrame projects={[]} projectId={projectId}>
        <OverviewLoadingState />
      </PortalFrame>
    );
  }
  if (error || !projects) {
    return (
      <PortalFrame projects={[]} projectId={projectId}>
        <CenteredMessage>
          <Alert severity="error" sx={{ maxWidth: 480 }}>
            Something went wrong loading your projects. Please try again in a moment.
          </Alert>
        </CenteredMessage>
      </PortalFrame>
    );
  }

  // No project picked yet: one running project opens directly; otherwise the
  // chooser (or, with nothing running, the project history) is the screen.
  if (!projectId) {
    const current = projects.filter((p) => p.isCurrent);
    if (current.length === 1) return <Navigate to={`/portal/${current[0].id}`} replace />;
    return (
      <PortalFrame projects={projects} projectId={undefined}>
        <ProjectChooser projects={projects} />
      </PortalFrame>
    );
  }

  return (
    <PortalFrame projects={projects} projectId={projectId}>
      <ProjectOverview projectId={projectId} />
    </PortalFrame>
  );
}

// ── Frame: top bar with switcher + logout ────────────────────────────────────

function PortalFrame({
  projects,
  projectId,
  children,
}: {
  projects: PortalProjectSummary[];
  projectId: string | undefined;
  children: React.ReactNode;
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" color="default" elevation={1} sx={{ bgcolor: 'background.paper' }}>
        <Toolbar sx={{ gap: 2 }}>
          <Box
            component="img"
            src={logo}
            alt="Dikonek"
            sx={{ height: 36, width: 'auto', flexShrink: 0, display: 'block' }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
            Client portal
          </Typography>
          <Box sx={{ flex: 1 }} />
          {projects.length > 1 && (
            <TextField
              select
              size="small"
              label="Project"
              value={projectId && projects.some((p) => p.id === projectId) ? projectId : ''}
              onChange={(e) => navigate(`/portal/${e.target.value}`)}
              sx={{ minWidth: { xs: 140, sm: 240 } }}
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                  {!p.isCurrent ? ' (finished)' : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          {user && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              {user.name}
            </Typography>
          )}
          <Button size="small" startIcon={<LogoutIcon />} onClick={logout}>
            Sign out
          </Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Box>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      {children}
    </Box>
  );
}

// ── Chooser / history ────────────────────────────────────────────────────────

function ProjectChooser({ projects }: { projects: PortalProjectSummary[] }) {
  const current = projects.filter((p) => p.isCurrent);
  const past = projects.filter((p) => !p.isCurrent);

  if (projects.length === 0) {
    return (
      <CenteredMessage>
        <Stack alignItems="center" gap={1} sx={{ textAlign: 'center', maxWidth: 420 }}>
          <FolderOpenIcon sx={{ fontSize: 48, color: 'grey.400' }} />
          <Typography variant="h6" fontWeight={700}>
            No projects yet
          </Typography>
          <Typography color="text.secondary">
            When Dikonek starts a project for you, its live progress report will appear here.
          </Typography>
        </Stack>
      </CenteredMessage>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack gap={4}>
        {current.length > 0 && (
          <ProjectCardList
            title="Your projects"
            subtitle="Pick a project to see its live progress report."
            projects={current}
          />
        )}
        {past.length > 0 && (
          <ProjectCardList
            title={current.length > 0 ? 'Project history' : 'Your project history at Dikonek'}
            subtitle={
              current.length > 0
                ? undefined
                : 'No project is running right now — here is everything Dikonek has delivered for you.'
            }
            icon={<HistoryIcon fontSize="small" />}
            projects={past}
          />
        )}
      </Stack>
    </Container>
  );
}

function ProjectCardList({
  title,
  subtitle,
  icon,
  projects,
}: {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  projects: PortalProjectSummary[];
}) {
  const navigate = useNavigate();
  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ color: 'text.secondary' }}>
        {icon}
        <Typography variant="h6" fontWeight={700} color="text.primary">
          {title}
        </Typography>
      </Stack>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {subtitle}
        </Typography>
      )}
      <Stack gap={1.5} sx={{ mt: subtitle ? 0 : 1.5 }}>
        {projects.map((p) => (
          <Card key={p.id} variant="outlined" sx={{ borderRadius: 3 }}>
            <CardActionArea onClick={() => navigate(`/portal/${p.id}`)}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>
                      {p.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.code}
                      {p.startDate ? ` · started ${formatDate(p.startDate)}` : ''}
                      {p.actualEndDate
                        ? ` · delivered ${formatDate(p.actualEndDate)}`
                        : p.expectedEndDate
                          ? ` · target ${formatDate(p.expectedEndDate)}`
                          : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" gap={1.5} alignItems="center" flexShrink={0}>
                    <Chip
                      size="small"
                      label={humanizeStatus(p.status)}
                      color={statusColor(p.status)}
                      variant="outlined"
                    />
                    <Typography fontWeight={700}>{p.overallPct}%</Typography>
                  </Stack>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={p.overallPct}
                  color={p.overallPct === 100 ? 'success' : 'primary'}
                  sx={{ mt: 1.5, height: 6, borderRadius: 3 }}
                />
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}

// ── Overview (the report itself) ─────────────────────────────────────────────

function ProjectOverview({ projectId }: { projectId: string }) {
  const { data, isLoading, error } = usePortalProjectView(projectId);
  const usePhotoSrc = (photoId: string) => usePortalPhotoSrc(projectId, photoId);

  // The download route needs the bearer token, so fetch a blob then save it.
  const downloadFile = async (file: ShareFile) => {
    const blob = await opsApi.portalFileBlob(projectId, file.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.originalName;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) return <OverviewLoadingState />;
  if (error || !data) {
    return (
      <CenteredMessage>
        <Alert severity="error" sx={{ maxWidth: 480 }}>
          This project could not be loaded. Pick another project or try again in a moment.
        </Alert>
      </CenteredMessage>
    );
  }
  // 64 = the sticky AppBar (Toolbar) height, so the stat bar pins right below it.
  return (
    <ProjectOverviewReport
      data={data}
      usePhotoSrc={usePhotoSrc}
      fetchUpdatesPage={(page, pageSize) => opsApi.portalUpdatesPage(projectId, page, pageSize)}
      updatesSurfaceKey={`portal:${projectId}`}
      onDownloadFile={downloadFile}
      stickyTop={64}
    />
  );
}
